import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import type { Resource } from '../lib/useResource'
import { initials } from '../lib/format'

// ── surfaces ─────────────────────────────────────────────────────────────────

export function Card({
  title,
  hint,
  action,
  flush,
  children,
}: {
  title?: ReactNode
  hint?: ReactNode
  action?: ReactNode
  flush?: boolean
  children: ReactNode
}) {
  return (
    <section className={flush ? 'card card--flush' : 'card'}>
      {(title || action) && (
        <header className="card__head" style={flush ? { padding: '18px 18px 0' } : undefined}>
          <div>
            <div className="card__title">{title}</div>
            {hint && <div className="card__hint">{hint}</div>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  )
}

export function Kpi({
  label,
  value,
  foot,
  tone,
}: {
  label: string
  value: ReactNode
  foot?: ReactNode
  tone?: 'green' | 'lime' | 'orange' | 'cyan' | 'alert'
}) {
  const color = tone ? `var(--${tone === 'alert' ? 'alert-bright' : tone})` : undefined
  return (
    <div className="card">
      <div className="kpi__label">{label}</div>
      <div className="kpi__value" style={{ color }}>
        {value}
      </div>
      {foot && <div className="kpi__foot">{foot}</div>}
    </div>
  )
}

export function Badge({
  children,
  tone,
}: {
  children: ReactNode
  tone?: 'green' | 'lime' | 'orange' | 'cyan' | 'alert'
}) {
  return <span className={tone ? `badge badge--${tone}` : 'badge'}>{children}</span>
}

export function Avatar({ name, url, large }: { name: string; url?: string | null; large?: boolean }) {
  const className = large ? 'avatar avatar--lg' : 'avatar'
  if (url) return <img className={className} src={url} alt="" />
  return <span className={className}>{initials(name)}</span>
}

export function Notice({
  children,
  tone,
}: {
  children: ReactNode
  tone?: 'warn' | 'phi'
}) {
  return <div className={tone ? `notice notice--${tone}` : 'notice'}>{children}</div>
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>
}

// ── state wrappers ───────────────────────────────────────────────────────────

export function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="stack" style={{ padding: 18 }} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${100 - i * 12}%` }} />
      ))}
    </div>
  )
}

/**
 * Renders a resource's three states with one component so no screen invents its
 * own way of saying "you are not allowed to see this".
 */
export function Loaded<T>({
  resource,
  children,
  emptyWhen,
  empty,
}: {
  resource: Resource<T>
  children: (data: T) => ReactNode
  emptyWhen?: (data: T) => boolean
  empty?: ReactNode
}) {
  if (resource.loading && resource.data === null) return <Loading />
  if (resource.forbidden) {
    return (
      <Empty>
        Your roles do not include the permission this view requires. Ask a superadmin if you
        need it — the request will be recorded in the audit log.
      </Empty>
    )
  }
  if (resource.error) {
    return (
      <div className="stack" style={{ padding: 18 }}>
        <Notice tone="warn">{resource.error.message}</Notice>
        <div>
          <button className="btn btn--sm" onClick={resource.reload}>
            Retry
          </button>
        </div>
      </div>
    )
  }
  if (resource.data === null) return <Empty>No data.</Empty>
  if (emptyWhen?.(resource.data)) return <Empty>{empty ?? 'Nothing here yet.'}</Empty>
  return <>{children(resource.data)}</>
}

// ── table ────────────────────────────────────────────────────────────────────

export type Column<T> = {
  key: string
  header: ReactNode
  render: (row: T) => ReactNode
  strong?: boolean
  width?: string
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty = 'Nothing here yet.',
}: {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  empty?: ReactNode
}) {
  if (rows.length === 0) return <Empty>{empty}</Empty>
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={c.width ? { width: c.width } : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={onRowClick ? 'clickable' : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((c) => (
                <td key={c.key} className={c.strong ? 'strong' : undefined}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── charts (inline SVG — no chart dependency) ────────────────────────────────

export function BarChart({
  data,
  height = 140,
  color = 'var(--green)',
  format = (v: number) => String(v),
}: {
  data: { label: string; value: number }[]
  height?: number
  color?: string
  format?: (value: number) => string
}) {
  if (data.length === 0) return <Empty>No samples in this window.</Empty>
  const max = Math.max(...data.map((d) => d.value), 1)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height, padding: '8px 0' }}>
      {data.map((d) => (
        <div
          key={d.label}
          title={`${d.label}: ${format(d.value)}`}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 6 }}
        >
          <div
            style={{
              height: `${Math.max((d.value / max) * (height - 28), d.value > 0 ? 3 : 1)}px`,
              background: d.value > 0 ? color : 'var(--chip)',
              borderRadius: 6,
            }}
          />
          <div
            className="dim"
            style={{ fontSize: 9.5, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden' }}
          >
            {d.label}
          </div>
        </div>
      ))}
    </div>
  )
}

const SERIES_COLORS = ['var(--green)', '--lime', '--cyan', '--orange', '--alert-bright'].map(
  (c) => (c.startsWith('var') ? c : `var(${c})`),
)

export function StackedBar({
  segments,
  format = (v: number) => String(v),
}: {
  segments: { label: string; value: number }[]
  format?: (value: number) => string
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0)
  if (total <= 0) return <Empty>No spend recorded yet.</Empty>
  return (
    <div className="stack" style={{ gap: 10 }}>
      <div style={{ display: 'flex', height: 12, borderRadius: 99, overflow: 'hidden' }}>
        {segments.map((s, i) => (
          <div
            key={s.label}
            title={`${s.label}: ${format(s.value)}`}
            style={{
              width: `${(s.value / total) * 100}%`,
              background: SERIES_COLORS[i % SERIES_COLORS.length],
            }}
          />
        ))}
      </div>
      <div className="row row--wrap" style={{ gap: 14 }}>
        {segments.map((s, i) => (
          <span key={s.label} className="row" style={{ gap: 6, fontSize: 11.5 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 99,
                background: SERIES_COLORS[i % SERIES_COLORS.length],
              }}
            />
            <span className="muted">{s.label}</span>
            <span className="num">{format(s.value)}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

export function Meter({ value, max, tone }: { value: number; max: number; tone?: string }) {
  const pct = max > 0 ? Math.min(value / max, 1) : 0
  return (
    <div className="meter">
      <div
        className="meter__fill"
        style={{ width: `${pct * 100}%`, background: tone ?? undefined }}
      />
    </div>
  )
}

// ── modal + reason prompt ────────────────────────────────────────────────────

export function Modal({
  title,
  children,
  onClose,
  wide,
}: {
  title: ReactNode
  children: ReactNode
  onClose: () => void
  /** For dialogs holding a canvas or a side-by-side, where the default column
   *  width would force horizontal scrolling on the content itself. */
  wide?: boolean
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="modal__scrim" onClick={onClose} role="presentation">
      <div
        className={wide ? 'modal modal--wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal__title">{title}</div>
        {children}
      </div>
    </div>
  )
}

/**
 * Every mutating action in this console asks for a reason, because every
 * mutating endpoint records one. The dialog enforces the same minimum length
 * the API does so the round-trip does not fail on a two-character answer.
 */
export function ReasonDialog({
  title,
  description,
  confirmLabel = 'Confirm',
  danger,
  minLength = 3,
  onConfirm,
  onClose,
}: {
  title: ReactNode
  description?: ReactNode
  confirmLabel?: string
  danger?: boolean
  minLength?: number
  onConfirm: (reason: string) => Promise<void> | void
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      await onConfirm(reason.trim())
      onClose()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={title} onClose={onClose}>
      <div className="stack">
        {description && <p className="muted" style={{ margin: 0 }}>{description}</p>}
        <div className="field">
          <label className="field__label" htmlFor="reason">
            Reason (recorded in the audit log, minimum {minLength} characters)
          </label>
          <textarea
            id="reason"
            className="textarea"
            style={{ minHeight: 84 }}
            value={reason}
            autoFocus
            onChange={(event) => setReason(event.target.value)}
            placeholder="Ticket reference and what prompted this"
          />
        </div>
        {error && <Notice tone="warn">{error}</Notice>}
        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className={danger ? 'btn btn--danger' : 'btn btn--primary'}
            disabled={busy || reason.trim().length < minLength}
            onClick={() => void submit()}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ── page scaffold ────────────────────────────────────────────────────────────

export function Page({
  title,
  subtitle,
  actions,
  children,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1 className="page__title">{title}</h1>
          {subtitle && <div className="page__sub">{subtitle}</div>}
        </div>
        {actions && <div className="row row--wrap">{actions}</div>}
      </header>
      {children}
    </div>
  )
}
